import { stateConvertEmbeds, stateIsEmpty } from '@/logic/stateReducers'
import { validateState } from '@/logic/validation'
import { ProfileType } from '@/types/api'
import useApi from 'api'
import React from 'react'

import Store from 'store'
import EditingContext from 'store/editingContext'
import Dialog from 'ui/dialog'
import { ErrorPopup } from 'ui/utils/Overlay'
import { OverlayContext, OverlayProps } from 'ui/utils/OverlayManager'

const OverwriteDialog = ({ onResolve, onClose }: OverlayProps<true>) => (
  <Dialog title={`Load profile`} modal onClose={onClose!}>
    <Dialog.Content>
      <p>{`Discard unsaved changes?`}</p>
    </Dialog.Content>
    <Dialog.Footer>
      <button onClick={() => onResolve!(true)}>Confirm</button>
      <button onClick={onClose}>Cancel</button>
    </Dialog.Footer>
  </Dialog>
)

export default function useLoadProfile(type: ProfileType) {
  const overlays = React.useContext(OverlayContext)
  const setSpinner = EditingContext.useStateSetter('spinner')
  const api = useApi()

  const mainStore = Store.useStore()
  const dispatch = Store.useDispatch()

  return React.useCallback(
    (id: string) => {
      setSpinner((spin) => spin + 1)
      api
        .load(id)
        .then(
          async (result) => {
            const { data, ...imported } = result
            let newState = await validateState(JSON.parse(data), imported)
            if (type === 'planner') newState = stateConvertEmbeds(newState)
            if (stateIsEmpty(newState, type)) {
              overlays.async(<ErrorPopup message={`Empty profile!`} />)
              return
            }

            const state = mainStore.getState()
            const isEmpty = stateIsEmpty(state, type)
            if (!isEmpty && !(state.imported && state.imported.modified !== 'heavy')) {
              if (!(await overlays.async(<OverwriteDialog />))) return
            }
            dispatch.setState(newState)
          },
          (error) => {
            overlays.async(<ErrorPopup message={error.message} />)
          }
        )
        .finally(() => setSpinner((spin) => spin - 1))
    },
    [api, overlays, setSpinner, mainStore, dispatch, type]
  )
}
